import Backbone from 'backbone'
import FormView from 'scoreboard/components/form-view'
import formHelper from 'scoreboard/components/form-helper'
import _ from 'lodash'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import selects from 'scoreboard/components/selects'
import errorHandler from 'scoreboard/components/error-handler'
import template from './single-formfield-dialog.hbs'
/**
 * Shared component that can be used to create or edit a single field for an
 * entity represented with a Backbone.Model.  A plain object can be provided
 * instead if it has a 'isNew' property that returns boolean or a function, and
 * you define the `onSubmit` option.
 *
 * Typically, this is useful for creating/editing a domain object (e.g. dataset tree)
 * where only the label is being updated.  But, this could be anything inside of a
 * gardener (tree).
 *
 * When the form is submitted a Backbone 'add:' or 'edit:' event will be triggered.
 *
 * Example: (from admin-manage-dataset-trees-panel.js)
 *
 *  Backbone.on('add:dataset-tree', this._onAdd.bind(this))
 *  ...
 *	appDisplay.dialog(new SingleFormFieldDialog({
 *		model: new DatasetTree(tree),
 *		heading: i18n.get('common.edit2', 'datasets.dataType.rollupTree'),
 *		fieldLabel: 'common.name',
 *		fieldName: 'label'
 *	}))
 *
 * Example for a Select:
 *
 *  appDisplay.dialog(new SingleFormFieldDialog({
 *      ...
 *		selectOptions: {
 *		  options,
 *		  selected: this.selectedValue,
 *		  ...
 *		}
 *	}))
 *
 *
 * @type {any}
 */

var SingleFormFieldDialog = Backbone.View.extend({

	tagName: 'form',

	className: 'single-form-field-dialog',

	/**
	 *
	 * @param options.heading The heading for the dialog, must be escaped by caller.
	 *
	 * @param options.fieldLabel The label for the text input
	 *
	 * @param [options.fieldName=label] The name that will be set on the text input
	 * label is the default because it's most common to be creating or renaming a model
	 *
	 * @param [options.selectOptions] If the field being shown is a select, then
	 * pass the options for the `selects` component in this option.
	 *
	 * @param options.primaryButtonLabel=['Save']
	 *
	 * @param {Function} options.onSubmit The default is to use the model.save()
	 * function with the form data.  If you need to take another action, then
	 * provide a function: (formData) => {}
	 *
	 * @param {String} [options.eventDomainType=model.domainType] Allows a more specific
	 * domain type to be specified. (e.g. instead of 'dataset-tree', 'dataset-tree-node')
	 */
	initialize: function(options= {}) {
		this.options = _.extend({
			fieldName: 'label',
			onSubmit: this._onSubmit.bind(this)
		}, options)
		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			guard: false
		})
	},

	render: function() {

		const context = _.extend({
			primaryButtonLabel: 'common.save'
		}, this.options)

		this.$el.empty().append(template(context))

		const {selectOptions} = this.options
		if (selectOptions) {
			this.$('.input-field').remove()
			selects(this.$('select'), selectOptions)
		} else {
			this.$('.select-field').remove()
		}

		formHelper.writeToDom(this.model, this.el)

		this.markFields()

		this.formView.start({
			isNew: _.result(this.model, 'isNew')
		})

		return this
	},

	markFields: function() {
		this.markedFields = []
		_.each(this.$('input'), function(input) {
			this.markedFields.push(new MarkedField({
				el: input,
				validator: requiredValidator
			}).render())
		}.bind(this))
	},

	submit: function() {
		var data = formHelper.readFromDom(this.el)
		this.options.onSubmit(data)
	},

	_onSubmit: function(data) {
		const error = (model, response) => errorHandler(response, {
			formView: this.formView,
			handler: this.options.errorHandler
		})
		var action = this.model.isNew() ? 'add' : 'edit'
		this.model.save(data, {
			error,
			success: () => {
				this.trigger('done', this.model)

				// For gardeners
				Backbone.trigger(`${action}:domain-object`,
					this.model, this.model.get('parentId'))

				// For other components, so they don't have to test the domain type
				const domainType = _.defaultTo(this.options.eventDomainType, this.model.domainType)
				Backbone.trigger(`${action}:${domainType}`,
					this.model, this.model.get('parentId'))
			}
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.markedFields], 'remove')
	}

})

export default SingleFormFieldDialog

SingleFormFieldDialog.__filename__ = 'components/single-formfield-dialog.js'
